<template>
  <v-container>
    <v-row v-if="loadingRecords" justify="center" align="center">
      <v-progress-circular indeterminate :size="60" color="primary" />
    </v-row>

    <v-row v-else-if="APIError" justify="center" align="center">
      <p>
        <v-icon color="error">mdi-alert-circle-outline</v-icon> Sorry, something
        went wrong. Please try again later.
      </p>
    </v-row>

    <v-container v-else-if="exercises">
      <v-row justify="center">
        <v-col lg="6" cols="12">
          <v-select
            :items="selectWorkoutPlans"
            v-model="selectedWorkoutPlanId"
            label="Workout Plan"
            outlined
          ></v-select>
        </v-col>
      </v-row>
      <v-row>
        <v-col
          v-for="exercise in selectedWorkoutPlan.exercises"
          :key="exercise.id"
          lg="3"
          cols="12"
        >
          <v-card :disabled="exercises[exercise.id]['completed']">
            <v-card-text>
              <p class="title mb-1">{{ exercise.name }}</p>
              <p class="overline">
                {{ `(${exercise.minReps}-${exercise.maxReps} reps)` }}
              </p>
              <v-form>
                <v-text-field
                  v-model="exercises[exercise.id].weight"
                  :placeholder="exercises[exercise.id].previousWeight"
                  label="Weight (kg)"
                ></v-text-field>
                <v-text-field
                  v-model="exercises[exercise.id].firstSetReps"
                  :placeholder="exercises[exercise.id].previousFirstSetReps"
                  label="1 Set"
                ></v-text-field>
                <v-text-field
                  v-model="exercises[exercise.id].secondSetReps"
                  :placeholder="exercises[exercise.id].previousSecondSetReps"
                  label="2 Set"
                ></v-text-field>
                <v-text-field
                  v-model="exercises[exercise.id].thirdSetReps"
                  :placeholder="exercises[exercise.id].previousThirdSetReps"
                  label="3 Set"
                ></v-text-field>
              </v-form>
            </v-card-text>
            <v-card-actions>
              <v-btn
                v-if="!exercises[exercise.id]['completed']"
                outlined
                color="primary"
                :loading="exercises[exercise.id]['isWaitingForResponse']"
                @click="submitExercise(exercise.id)"
              >
                Complete
              </v-btn>
              <v-btn v-else text color="success">Completed</v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-container>
</template>

<script>
import axios from "axios";

import { mapState } from 'vuex'

export default {
  name: "TrainingWorkout",

  data: () => ({
    APIError: false,
    loadingRecords: false,
    selectedWorkoutPlanId: null,
    exercises: null
  }),

  mounted() {
    if (this.workoutPlans.length == 0 || this.completedExercises.length == 0) {
      this.getData()
    } else {
      this.updateExercises(this.workoutPlans, this.completedExercises)
    }
  },

  computed: {
    ...mapState([
      'workoutPlans',
      'completedExercises',
    ]),
    selectedWorkoutPlan: function () {
      return this.workoutPlans.find(
        workoutPlan => workoutPlan.id === this.selectedWorkoutPlanId
      )
    },
    selectWorkoutPlans: function () {
      let workoutPlans = []
      this.workoutPlans.forEach(workoutPlan => {
        workoutPlans.push({
          "text": workoutPlan.name,
          "value": workoutPlan.id
        })
      })
      return workoutPlans
    }
  },

  methods: {
    getWorkoutPlans: function () {
      return axios.get(
        `${process.env.VUE_APP_API_ROOT_URL}training/plans/`,
        this.$store.getters.axiosConfig
      )
    },
    getCompletedExercises: function () {
      return axios.get(
        `${process.env.VUE_APP_API_ROOT_URL}training/completed-exercises/`,
        this.$store.getters.axiosConfig
      )
    },
    getData: function () {
      this.loadingRecords = true
      this.APIError = false
      Promise.all([this.getWorkoutPlans(), this.getCompletedExercises()])
        .then(([workoutPlans, completedExercises]) => {
          this.$store.commit("updateWorkoutPlans", workoutPlans.data)
          this.$store.commit("updateCompletedExercises", completedExercises.data)
          this.updateExercises(workoutPlans.data, completedExercises.data)
          this.loadingRecords = false
        })
        .catch(() => {
          this.APIError = true
          this.loadingRecords = false
        })
    },
    updateExercises: function (workoutPlans, completedExercises) {
      let exercises = {}
      workoutPlans.forEach(workoutPlan => {
        workoutPlan.exercises.forEach(exercise => {
          const lastCompleted = completedExercises.find(completedExercise => {
            return completedExercise.exercise === exercise.id
          })
          exercises[exercise.id] = {
            weight: null,
            firstSetReps: null,
            secondSetReps: null,
            thirdSetReps: null,
            completed: lastCompleted
              ? (lastCompleted.date === new Date().toISOString().slice(0, 10)) ? true : false
              : false,
            isWaitingForResponse: false,
            previousWeight: lastCompleted
              ? lastCompleted.weight.toString()
              : "13",
            previousFirstSetReps: lastCompleted
              ? lastCompleted.firstSetReps.toString()
              : exercise.minReps.toString(),
            previousSecondSetReps: lastCompleted
              ? lastCompleted.secondSetReps.toString()
              : exercise.minReps.toString(),
            previousThirdSetReps: lastCompleted
              ? lastCompleted.thirdSetReps.toString()
              : exercise.minReps.toString(),
          }
        })
      });
      this.selectedWorkoutPlanId = workoutPlans[0].id
      this.exercises = exercises
    },
    submitExercise: function (id) {
      const exercise = this.exercises[id]
      const completedExercise = {
        exercise: id,
        weight: exercise.weight || exercise.previousWeight,
        firstSetReps: exercise.firstSetReps || 0,
        secondSetReps: exercise.secondSetReps || 0,
        thirdSetReps: exercise.thirdSetReps || 0,
      }
      this.$set(this.exercises[id], "isWaitingForResponse", true)
      axios
        .post(
          `${process.env.VUE_APP_API_ROOT_URL}training/completed-exercises/`,
          completedExercise,
          this.$store.getters.axiosConfig
        )
        .then((response) => {
          this.$store.commit("updateMoodDiaryRecord", response.data)
          this.$set(this.exercises[id], "isWaitingForResponse", false)
          this.$set(this.exercises[id], "completed", true)
        })
        .catch(() => {
          this.APIError = true
          this.$set(this.exercises[id], "isWaitingForResponse", false)
        });
    }
  }
}
</script>